import React, { useCallback, useRef, useState } from 'react';
import { toast } from 'sonner';
import { api } from '@/lib/api';
import { UploadCloud, X, Loader2 } from 'lucide-react';

/**
 * Drag-and-drop image uploader.
 *
 * Props:
 *  - multiple: bool (default false) — array of URLs vs single URL
 *  - value: string | string[]
 *  - onChange: (newValue) => void
 *  - label?: string (small helper text)
 */
const ImageUploader = ({ multiple = false, value, onChange, label }) => {
  const [dragOver, setDragOver] = useState(false);
  const [uploading, setUploading] = useState(false);
  const inputRef = useRef(null);

  const urls = multiple
    ? (Array.isArray(value) ? value : (typeof value === 'string' && value ? value.split('\n').map((s) => s.trim()).filter(Boolean) : []))
    : (value || '');

  const uploadFiles = useCallback(async (fileList) => {
    const files = Array.from(fileList || []).filter((f) => f.type.startsWith('image/'));
    if (files.length === 0) { toast.error('Please drop image files (JPG, PNG, WEBP or GIF)'); return; }
    setUploading(true);
    try {
      if (multiple) {
        const fd = new FormData();
        files.slice(0, 12).forEach((f) => fd.append('files', f));
        const { data } = await api.post('/admin/uploads/images', fd, { headers: { 'Content-Type': 'multipart/form-data' } });
        const newUrls = data.uploads.map((u) => u.url);
        onChange([...(Array.isArray(urls) ? urls : []), ...newUrls]);
        toast.success(`Uploaded ${newUrls.length} image${newUrls.length > 1 ? 's' : ''}`);
      } else {
        const fd = new FormData();
        fd.append('file', files[0]);
        const { data } = await api.post('/admin/uploads/image', fd, { headers: { 'Content-Type': 'multipart/form-data' } });
        onChange(data.url);
        toast.success('Image uploaded');
      }
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Upload failed');
    } finally {
      setUploading(false);
      if (inputRef.current) inputRef.current.value = '';
    }
  }, [multiple, urls, onChange]);

  const onDrop = (e) => {
    e.preventDefault();
    setDragOver(false);
    if (uploading) return;
    uploadFiles(e.dataTransfer.files);
  };

  const removeAt = (idx) => {
    if (multiple) {
      const next = [...(Array.isArray(urls) ? urls : [])];
      next.splice(idx, 1);
      onChange(next);
    } else {
      onChange('');
    }
  };

  const hasImages = multiple ? (Array.isArray(urls) && urls.length > 0) : !!urls;

  return (
    <div className="space-y-3">
      {/* Preview grid */}
      {hasImages && (
        <div className={multiple ? 'grid grid-cols-3 sm:grid-cols-4 gap-3' : ''}>
          {multiple ? (
            urls.map((u, i) => (
              <div key={`${u}-${i}`} className="relative group border border-sans_line bg-sans_bone aspect-square overflow-hidden">
                <img src={u} alt="" className="w-full h-full object-cover" />
                <button
                  type="button"
                  onClick={() => removeAt(i)}
                  className="absolute top-1 right-1 w-7 h-7 rounded-full bg-black/70 text-white flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity hover:bg-black"
                  data-testid={`image-remove-${i}`}
                  aria-label="Remove"
                >
                  <X className="w-4 h-4" strokeWidth={2} />
                </button>
                {i === 0 && <div className="absolute bottom-1 left-1 text-[10px] uppercase tracking-widest bg-sans_ink text-sans_bone px-1.5 py-0.5">Cover</div>}
              </div>
            ))
          ) : (
            <div className="relative w-40 h-40 border border-sans_line bg-sans_bone overflow-hidden">
              <img src={urls} alt="" className="w-full h-full object-cover" />
              <button
                type="button"
                onClick={() => removeAt(0)}
                className="absolute top-1 right-1 w-7 h-7 rounded-full bg-black/70 text-white flex items-center justify-center hover:bg-black"
                aria-label="Remove"
                data-testid="image-remove-single"
              >
                <X className="w-4 h-4" strokeWidth={2} />
              </button>
            </div>
          )}
        </div>
      )}

      {/* Drop zone */}
      <div
        onDragOver={(e) => { e.preventDefault(); setDragOver(true); }}
        onDragLeave={() => setDragOver(false)}
        onDrop={onDrop}
        onClick={() => !uploading && inputRef.current?.click()}
        className={`cursor-pointer border-2 border-dashed transition-colors py-8 px-4 text-center ${
          dragOver ? 'border-sans_ink bg-sans_bone' : 'border-sans_line bg-white hover:border-sans_ink'
        } ${uploading ? 'opacity-60 cursor-wait' : ''}`}
        data-testid={multiple ? 'gallery-dropzone' : 'image-dropzone'}
      >
        <input
          ref={inputRef}
          type="file"
          accept="image/jpeg,image/png,image/webp,image/gif"
          multiple={multiple}
          onChange={(e) => uploadFiles(e.target.files)}
          className="hidden"
        />
        {uploading ? (
          <div className="flex items-center justify-center gap-2 text-sm text-sans_mute">
            <Loader2 className="w-4 h-4 animate-spin" /> Uploading…
          </div>
        ) : (
          <div className="flex flex-col items-center gap-2">
            <UploadCloud className="w-8 h-8 text-sans_mute" strokeWidth={1.25} />
            <div className="text-sm text-sans_ink">
              {hasImages ? (multiple ? 'Drop more images or click to browse' : 'Drop a new image or click to replace') : (multiple ? 'Drop images here or click to browse' : 'Drop an image here or click to browse')}
            </div>
            <div className="text-xs text-sans_mute">
              JPG · PNG · WEBP · GIF · up to 10 MB each{multiple ? ' · up to 12 at a time' : ''}
            </div>
          </div>
        )}
      </div>

      {label && <div className="text-xs text-sans_mute">{label}</div>}
    </div>
  );
};

export default ImageUploader;
